export interface CompressionOptions {
  maxSizeMB?: number;
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Canvas-based image compression
 * Compress an image file using HTML5 Canvas
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<File> - The compressed file
 */
const canvasCompress = async (
  file: File,
  options: CompressionOptions
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    const img = new Image();
    let objectUrl: string | null = null;

    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    img.onload = () => {
      cleanup(); // Clean up object URL immediately after loading
      
      const { maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = options;
      
      // Calculate new dimensions
      let { width, height } = img;
      const aspectRatio = width / height;

      if (width > maxWidth) {
        width = maxWidth;
        height = width / aspectRatio;
      }
      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create compressed blob'));
          return;
        }
        
        const compressedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now()
        });
        
        resolve(compressedFile);
      }, file.type, quality);
    };

    img.onerror = () => {
      cleanup();
      reject(new Error('Failed to load image for compression'));
    };

    try {
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (error) {
      reject(new Error(`Failed to create object URL: ${error instanceof Error ? error.message : 'Unknown error'}`));
    }
  });
};

/**
 * compressImage
 * Compress an image file to reduce its size while maintaining quality
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<CompressionResult> - The compressed file with metadata
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const originalSize = file.size;

  try {
    // Validate input file
    if (!file || !(file instanceof File)) {
      throw new Error("Invalid file provided");
    }

    if (!file.type.startsWith("image/")) {
      throw new Error("File is not an image");
    }

    // Compress using canvas
    const compressedFile = await canvasCompress(file, options);
    const compressedSize = compressedFile.size;
    const compressionRatio = originalSize > 0 ? ((originalSize - compressedSize) / originalSize) * 100 : 0;

    console.log(`✅ Image compressed: ${file.name}`);
    console.log(`📊 Size: ${(originalSize / 1024 / 1024).toFixed(2)}MB → ${(compressedSize / 1024 / 1024).toFixed(2)}MB`);
    console.log(`📈 Compression: ${compressionRatio.toFixed(1)}% reduction`);

    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      compressionRatio
    };

  } catch (error) {
    console.error("❌ Compression error:", error);
    throw new Error(`Failed to compress image "${file.name}": ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Check if a file needs compression based on size
 * @param file - The file to check
 * @param maxSizeMB - Maximum size in MB (default: 5)
 * @returns boolean - True if file needs compression
 */
export const needsCompression = (file: File, maxSizeMB: number = 5): boolean => {
  if (!file || typeof file.size !== 'number') {
    return false;
  }
  return file.size > maxSizeMB * 1024 * 1024;
};

/**
 * Process multiple files, compressing those that exceed the size limit
 * @param files - Array of files to process
 * @param options - Compression options
 * @returns Promise<CompressionResult[]> - Array of processed files with metadata
 */
export const processImages = async (
  files: File[],
  options: CompressionOptions = {}
): Promise<CompressionResult[]> => {
  if (!Array.isArray(files)) {
    throw new Error("Files must be an array");
  }

  const results: CompressionResult[] = [];
  
  for (const file of files) {
    if (!file || !(file instanceof File)) {
      throw new Error("Invalid file in array");
    }

    if (!file.type.startsWith("image/")) {
      throw new Error(`"${file.name}" is not an image file`);
    }

    if (needsCompression(file, options.maxSizeMB || 5)) {
      // Compress the file
      const result = await compressImage(file, options);
      results.push(result);
    } else {
      // File doesn't need compression
      results.push({
        file,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 0
      });
    }
  }
  
  return results;
}; 